import { loadStripe } from '@stripe/stripe-js';
import { computed } from 'vue';
import ApiClient from '@/ApiClient';
import State from '@/state/State';
import { useStateComposable } from '@/state/composables';
import { toCurrency } from '@/utils';

const PLAN_DESCRIPTIONS = {
  premium: 'For teams who want to go above and beyond cost of living support.',
  standard: 'For teams who who want to offer comprehensive cost of living support.',
  entry: 'For teams who want to offer streamlined cost of living support.',
};

const PLAN_ID_PREFIX = 'prod_';
const PLAN_VARIANTS_PREFIX = 'per-seat-v2';

const { computedState, state, reset } = useStateComposable({
  plans: [],
  stripeKey: null,
  stripe: null,
  error: false,
  initial: true,
  loading: false,
});

const filteredPlans = computed(() => formatPaymentPlans(state.plans));

const formatPaymentPlans = plans => {
  const newPlans = [];

  const variant = State.state.company?.properties?.sme_properties?.registration_variant;
  let perSeatPlans = [];

  if (process.env.NODE_ENV === 'production') {
    if (variant) {
      perSeatPlans = plans.filter(plan => plan.id === `${PLAN_ID_PREFIX}${variant}`);
    } else {
      perSeatPlans = plans.filter(plan => plan.metadata.variant.startsWith(PLAN_VARIANTS_PREFIX));
    }
  } else {
    perSeatPlans = plans;
  }

  perSeatPlans.forEach(plan => {
    newPlans.push({
      ...plan,
      title: plan.description,
      body:
        `${toCurrency(plan.price.unit_amount)} per enrolled employee / month. ` +
        (PLAN_DESCRIPTIONS[plan.description.toLowerCase()] || 'Your agreed plan.'),
    });
  });
  return newPlans;
};

const getPaymentPlans = async (fetchOnlyIfInitial = false) => {
  if (fetchOnlyIfInitial && !state.initial) {
    return;
  }

  reset();

  state.initial = false;
  state.loading = true;

  let response;
  try {
    response = await ApiClient.smeGetPaymentPlans();
  } catch (error) {
    state.error = error?.message || 'Unable to fetch subscription plans. Please try again later';
    state.loading = false;
    return;
  }
  state.stripeKey = response.publishableKey;
  state.stripe = await loadStripe(state.stripeKey);
  state.plans = response.plans;
  state.loading = false;
};

const getPaymentPlan = planId => filteredPlans.value.find(plan => plan.id === `${PLAN_ID_PREFIX}${planId}`);
const hasPaymentPlan = planId => !!getPaymentPlan(planId);

export default () => {
  return {
    ...computedState,
    filteredPlans,
    getPaymentPlans,
    getPaymentPlan,
    hasPaymentPlan,
  };
};
